import { Tenancy, getTenancy } from "../../../../../api-client/APIClient";
import {
  EU_INVITE_SIGNUP_FORM_ID,
  EU_SIGNUP_PRE_EMAIL_CONFIRMATION_FORM_ID,
  EU_STANDARD_SIGNUP_FORM_ID,
  INVITE_SIGNUP_FORM_ID,
  STANDARD_SIGNUP_FORM_ID,
  STANDARD_SIGNUP_PRE_EMAIL_CONFIRMATION_FORM_ID,
} from "../HubspotForms";

interface Args {
  hasInviteToken: boolean;
  isSamlRequired: boolean;
  socialAuthProvider?: string | null;
}

interface SignupTelemetryIds {
  /** gtag id applied to the primary submit button on Step 2. */
  createAccountButtonGTag: string;
  /** gtag id applied to the form element (used by FinishSocialSignUp). */
  signupFormGTag: string;
  /** Hubspot form id submitted alongside successful account creation. */
  successFormId: string;
  /** Hubspot form id submitted when the verification email is sent (no token path). */
  preEmailConfirmationFormId: string;
}

/**
 * Resolves Hubspot form IDs and gtag identifiers from the current tenancy +
 * invite/social/SAML context. Extracted from the original SignUpScreen so the
 * coordinator stays focused on rendering decisions.
 */
const useSignupTelemetryIds = ({
  hasInviteToken,
  isSamlRequired,
  socialAuthProvider,
}: Args): SignupTelemetryIds => {
  const isEU = getTenancy() === Tenancy.EUMultiTenant;

  let createAccountButtonGTag: string;
  if (hasInviteToken) {
    createAccountButtonGTag = isEU ? "gtag-create-account-invite_eu" : "gtag-create-account-invite";
  } else if (socialAuthProvider) {
    createAccountButtonGTag = isEU
      ? `gtag-create-account-social-auth-${socialAuthProvider}_eu`
      : `gtag-create-account-social-auth-${socialAuthProvider}`;
  } else if (isSamlRequired) {
    createAccountButtonGTag = isEU
      ? "gtag-create-account-with-sso_eu"
      : "gtag-create-account-with-sso";
  } else {
    createAccountButtonGTag = isEU ? "gtag-create-account_eu" : "gtag-create-account";
  }

  // Hubspot form IDs that fire on successful account creation.
  let successFormId: string;
  if (hasInviteToken) {
    successFormId = isEU ? EU_INVITE_SIGNUP_FORM_ID : INVITE_SIGNUP_FORM_ID;
  } else {
    successFormId = isEU ? EU_STANDARD_SIGNUP_FORM_ID : STANDARD_SIGNUP_FORM_ID;
  }

  const preEmailConfirmationFormId = isEU
    ? EU_SIGNUP_PRE_EMAIL_CONFIRMATION_FORM_ID
    : STANDARD_SIGNUP_PRE_EMAIL_CONFIRMATION_FORM_ID;

  let signupFormGTag: string;
  if (hasInviteToken) {
    signupFormGTag = isEU ? "gtag-invite-form_eu" : "gtag-invite-form";
  } else {
    signupFormGTag = isEU ? "gtag-signup-form_eu" : "gtag-signup-form";
  }

  return {
    createAccountButtonGTag,
    signupFormGTag,
    successFormId,
    preEmailConfirmationFormId,
  };
};

export default useSignupTelemetryIds;
